import React, { useEffect, useState } from 'react';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import {useTranslate} from "../useTranslate";
import LimitByDate from "./limit-by-date";
import {isObject} from "../utils";

function LineChart({ chartData, className, dateLimit, setDateLimit, loading }) {
    const [chartDataState, setChartDataState] = useState({ labels: [], datasets: [] });
    const [chartOptions, setChartOptions] = useState({});
    const [datasetOptions, setDatasetOptions] = useState([]);
    const [selectedDataset, setSelectedDataset] = useState(null);
    const translate = useTranslate();


    const emptyChart = {
        labels: ['No Data'],
        datasets: [
            {
                label: 'Empty',
                data: [0],
                backgroundColor: 'lightgray',
                borderRadius: 8,
                maxBarThickness: 50,
            }
        ]
    };

    const emptyOptions = {
        maintainAspectRatio: false,
        aspectRatio: 1.5,
        plugins: {
            legend: {
                display: true,
                labels: {
                    font: { family: 'Shabnam-FD' }
                }
            },
            tooltip: { enabled: true }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: '',
                    font: { family: 'Shabnam-FD' }
                },
                ticks: {
                    font: { family: 'Shabnam-FD' }
                }
            },
            y: {
                title: {
                    display: true,
                    text: '',
                    font: { family: 'Shabnam-FD' }
                },
                ticks: {
                    font: { family: 'Shabnam-FD' }
                }
            }
        }
    };

    useEffect(() => {

        if (!chartData || !isObject(chartData)) {
            setChartDataState(emptyChart);
            setChartOptions(emptyOptions);
            setDatasetOptions([]);
            setSelectedDataset(null);
            return;
        }

        const options = Object.keys(chartData).map(key => ({ label: key, value: key }));
        setDatasetOptions(options);
        // Auto-select the first dataset if none selected
        if (options.length > 0) {
            setSelectedDataset(options[0].value);
        }
    }, [chartData]);

    useEffect(() => {
        setChartDataState({
            labels:[],
            datasets: [],
        });
        if (!selectedDataset || !chartData || !chartData[selectedDataset]) return;

        const {
            labels = [],
            datasets = [],
            orientation = 'v',
            xAxisLabel = '',
            yAxisLabel = ''
        } = chartData[selectedDataset];

        const chartOrientation = orientation === 'horizontal' ? 'y' : 'x';

        const allValues = datasets.flatMap(ds => ds.data || []);
        const maxY = allValues.length ? Math.max(...allValues) + 5 : undefined;

        setChartDataState({
            labels,
            datasets: datasets.map(ds => ({
                label: translate(ds.label) || translate("no-title"),
                data: ds.data || [],
                backgroundColor: ds.backgroundColor || 'gray',
                tension: ds.tension || 0,
                borderColor: ds.borderColor || 'gray',
                borderRadius: 8,
                maxBarThickness: 50,
            })),
        });

        setChartOptions({
            indexAxis: chartOrientation,
            maintainAspectRatio: false,
            aspectRatio: 1.5,
            plugins: {
                legend: {
                    display: true,
                    labels: {
                        font: { family: 'Shabnam-FD' }
                    }
                },
                tooltip: { enabled: true }
            },
            scales: {
                x: {
                    display: true,
                    suggestedMax: chartOrientation === 'y' ? maxY : undefined,
                    title: {
                        display: !!xAxisLabel,
                        text: translate(xAxisLabel),
                        font: { family: 'Shabnam-FD' }
                    },
                    ticks: {
                        font: { family: 'Shabnam-FD' }
                    }
                },
                y: {
                    display: true,
                    suggestedMax: chartOrientation === 'x' ? maxY : undefined,
                    title: {
                        display: !!yAxisLabel,
                        text: translate(yAxisLabel),
                        font: { family: 'Shabnam-FD' }
                    },
                    grid: {
                        drawTicks: false,
                        lineWidth: 2
                    },
                    border: {
                        display: false,
                        dash: [2, 3]
                    },
                    ticks: {
                        font: { family: 'Shabnam-FD' }
                    }
                }
            }
        });

    }, [selectedDataset, chartData]);

    return (
        <div className={`flex flex-column ${loading && "p-disabled"} w-full`}>
            <div className="flex align-items-center gap-2">
                {dateLimit&&<LimitByDate selectedLimit={dateLimit.value} onChange={(e)=>{setDateLimit(e)}}/>}
                {datasetOptions.length>1&&<Dropdown options={datasetOptions} value={selectedDataset} onChange={(e) => setSelectedDataset(e.value)}/>}
            </div>
            <Chart type="line" data={chartDataState} options={chartOptions} className={className} />
        </div>
    );
}

export default LineChart;
